import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DownloadCloud, Inbox, Loader2, PackageSearch, RotateCcw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { analytics } from '@/services/analytics';
import { logging } from '@/services/logging';
import {
  fetchConnectedEtsyShops,
  fetchImportedEtsyOrders,
  syncEtsyShop,
  updateEtsyImportedOrdersInboxSelection,
  type EtsyImportFilters,
  type EtsyPendingOrder,
  type EtsyShop,
} from '@/services/etsyService';
import EtsyWorkspaceNav from '@/components/etsy/EtsyWorkspaceNav';
import { WORKSPACE_CANVAS_MODES } from '@/components/workspace/workspaceMode';

const toDateInputValue = (date: Date) => date.toISOString().slice(0, 10);

const getDefaultDateFrom = () => {
  const date = new Date();
  date.setDate(date.getDate() - 7);
  return toDateInputValue(date);
};

const getDefaultDateTo = () => toDateInputValue(new Date());

const ETSY_IMPORT_FILTERS_STORAGE_KEY = 'tulasihome:etsy-import-filters';

type EtsyImportFilterState = {
  readonly shopId: string;
  readonly dateFrom: string;
  readonly dateTo: string;
  readonly onlyPaid: boolean;
  readonly onlyUnshipped: boolean;
  readonly onlyUndelivered: boolean;
};

const getDefaultImportFilterState = (): EtsyImportFilterState => ({
  shopId: '',
  dateFrom: getDefaultDateFrom(),
  dateTo: getDefaultDateTo(),
  onlyPaid: false,
  onlyUnshipped: false,
  onlyUndelivered: false,
});

const readStoredImportFilterState = (): EtsyImportFilterState => {
  const defaults = getDefaultImportFilterState();

  if (typeof window === 'undefined') {
    return defaults;
  }

  try {
    const stored = window.localStorage.getItem(ETSY_IMPORT_FILTERS_STORAGE_KEY);
    if (!stored) {
      return defaults;
    }

    const parsed = JSON.parse(stored) as Partial<EtsyImportFilterState>;
    return {
      shopId: typeof parsed.shopId === 'string' ? parsed.shopId : defaults.shopId,
      dateFrom: typeof parsed.dateFrom === 'string' ? parsed.dateFrom : defaults.dateFrom,
      dateTo: typeof parsed.dateTo === 'string' ? parsed.dateTo : defaults.dateTo,
      onlyPaid: typeof parsed.onlyPaid === 'boolean' ? parsed.onlyPaid : defaults.onlyPaid,
      onlyUnshipped: typeof parsed.onlyUnshipped === 'boolean' ? parsed.onlyUnshipped : defaults.onlyUnshipped,
      onlyUndelivered: typeof parsed.onlyUndelivered === 'boolean' ? parsed.onlyUndelivered : defaults.onlyUndelivered,
    };
  } catch {
    return defaults;
  }
};

const formatDateTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const getEtsyOrderTimestampMs = (order: EtsyPendingOrder) => {
  const timestamp = order.etsyData.paid_timestamp
    ?? order.etsyData.payment_timestamp
    ?? order.etsyData.create_timestamp
    ?? order.etsyData.created_timestamp;

  if (typeof timestamp === 'number' && Number.isFinite(timestamp)) {
    return timestamp * 1000;
  }

  const importedAt = new Date(order.importedAt).getTime();
  return Number.isNaN(importedAt) ? 0 : importedAt;
};

const formatEtsyOrderDateTime = (order: EtsyPendingOrder) => {
  const timestampMs = getEtsyOrderTimestampMs(order);
  return formatDateTime(new Date(timestampMs).toISOString());
};

const formatMoney = (order: EtsyPendingOrder) => {
  const amount = order.etsyData.grandtotal?.amount;
  const divisor = order.etsyData.grandtotal?.divisor;
  const currency = order.etsyData.grandtotal?.currency_code;

  if (!amount || !divisor || !currency) {
    return order.etsyData.total_price || 'n/a';
  }

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount / divisor);
  } catch {
    return `${currency} ${(amount / divisor).toFixed(2)}`;
  }
};

const buildSearchIndex = (order: EtsyPendingOrder) => [
  order.etsyReceiptId,
  order.shopName,
  order.etsyData.name,
  order.etsyData.buyer_email,
  order.etsyData.city,
  order.etsyData.country_iso,
  ...order.items.map((item) => item.title),
].filter(Boolean).join(' ').toLowerCase();

const getStatusTagClassName = (tone: 'success' | 'neutral' | 'inbox') => {
  const baseClassName = 'rounded-[6px] border px-2 py-0.5 text-[11px] font-medium shadow-none';

  switch (tone) {
    case 'success':
      return `${baseClassName} border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-50 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-200`;
    case 'inbox':
      return `${baseClassName} border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-50 dark:border-sky-900/60 dark:bg-sky-950/30 dark:text-sky-200`;
    case 'neutral':
    default:
      return `${baseClassName} border-border bg-muted/35 text-muted-foreground hover:bg-muted/35`;
  }
};

const EtsyImportCanvas = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const initialFilters = useMemo(readStoredImportFilterState, []);
  const [shops, setShops] = useState<ReadonlyArray<EtsyShop>>([]);
  const [shopId, setShopId] = useState<string>(initialFilters.shopId);
  const [dateFrom, setDateFrom] = useState(initialFilters.dateFrom);
  const [dateTo, setDateTo] = useState(initialFilters.dateTo);
  const [onlyPaid, setOnlyPaid] = useState(initialFilters.onlyPaid);
  const [onlyUnshipped, setOnlyUnshipped] = useState(initialFilters.onlyUnshipped);
  const [onlyUndelivered, setOnlyUndelivered] = useState(initialFilters.onlyUndelivered);
  const [orders, setOrders] = useState<ReadonlyArray<EtsyPendingOrder>>([]);
  const [selectedOrderIds, setSelectedOrderIds] = useState<ReadonlyArray<string>>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [moving, setMoving] = useState(false);

  const buildFilters = useCallback((): EtsyImportFilters => ({
    shopId: shopId || null,
    dateFrom: dateFrom || null,
    dateTo: dateTo || null,
    onlyPaid,
    onlyUnshipped,
    onlyUndelivered,
  }), [dateFrom, dateTo, onlyPaid, onlyUndelivered, onlyUnshipped, shopId]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(ETSY_IMPORT_FILTERS_STORAGE_KEY, JSON.stringify({
      shopId,
      dateFrom,
      dateTo,
      onlyPaid,
      onlyUnshipped,
      onlyUndelivered,
    }));
  }, [dateFrom, dateTo, onlyPaid, onlyUndelivered, onlyUnshipped, shopId]);

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      const result = await fetchImportedEtsyOrders({
        ...buildFilters(),
        includeCompleted: true,
      });
      setOrders(result);
      setSelectedOrderIds((current) => current.filter((id) => result.some((order) => order.id === id)));
    } catch (error) {
      logging.error('Failed to load Etsy import orders', { error });
      toast({
        title: t('integrations.etsy.toasts.importLoadFailedTitle', 'Could not load Etsy import orders'),
        description: error instanceof Error ? error.message : undefined,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [buildFilters, t, toast]);

  useEffect(() => {
    let cancelled = false;

    const loadShops = async () => {
      try {
        const result = await fetchConnectedEtsyShops();
        if (cancelled) {
          return;
        }
        setShops(result);
        if (result.length === 0) {
          navigate('/studio?canvas=etsy-setup');
        }
      } catch (error) {
        logging.error('Failed to load Etsy shops for import', { error });
      }
    };

    void loadShops();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  useEffect(() => {
    if (shops.length > 0) {
      const timeoutId = window.setTimeout(() => {
        void loadOrders();
      }, 350);

      return () => window.clearTimeout(timeoutId);
    }

    return undefined;
  }, [loadOrders, shops.length]);

  useEffect(() => {
    if (shopId && shops.length > 0 && !shops.some((shop) => shop.id === shopId)) {
      setShopId('');
    }
  }, [shopId, shops]);

  const filteredOrders = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    const matchedOrders = normalized
      ? orders.filter((order) => buildSearchIndex(order).includes(normalized))
      : orders;

    return [...matchedOrders].sort((left, right) => {
      const timestampDifference = getEtsyOrderTimestampMs(right) - getEtsyOrderTimestampMs(left);
      if (timestampDifference !== 0) {
        return timestampDifference;
      }

      return right.etsyReceiptId.localeCompare(left.etsyReceiptId);
    });
  }, [orders, query]);

  const visibleSelectableOrderIds = filteredOrders
    .filter((order) => !order.selectedForInbox)
    .map((order) => order.id);
  const selectedOrderIdSet = new Set(selectedOrderIds);
  const allVisibleSelected = visibleSelectableOrderIds.length > 0
    && visibleSelectableOrderIds.every((id) => selectedOrderIdSet.has(id));

  const toggleOrder = (orderId: string) => {
    setSelectedOrderIds((current) => (
      current.includes(orderId)
        ? current.filter((id) => id !== orderId)
        : [...current, orderId]
    ));
  };

  const toggleAllVisible = () => {
    setSelectedOrderIds((current) => {
      const currentSet = new Set(current);
      if (allVisibleSelected) {
        visibleSelectableOrderIds.forEach((id) => currentSet.delete(id));
      } else {
        visibleSelectableOrderIds.forEach((id) => currentSet.add(id));
      }
      return Array.from(currentSet);
    });
  };

  const handleResetFilters = () => {
    const defaults = getDefaultImportFilterState();
    setShopId(defaults.shopId);
    setDateFrom(defaults.dateFrom);
    setDateTo(defaults.dateTo);
    setOnlyPaid(defaults.onlyPaid);
    setOnlyUnshipped(defaults.onlyUnshipped);
    setOnlyUndelivered(defaults.onlyUndelivered);
    setQuery('');
    setSelectedOrderIds([]);
  };

  const handleSync = async () => {
    const shopsToSync = shopId
      ? shops.filter((shop) => shop.id === shopId)
      : shops;

    if (shopsToSync.length === 0) {
      return;
    }

    try {
      setSyncing(true);
      analytics.track('button: ui.etsy_import_sync_click', {
        shopId: shopId || 'all',
        shopCount: shopsToSync.length,
      });

      let found = 0;
      let imported = 0;
      let failed = 0;

      for (const shop of shopsToSync) {
        const result = await syncEtsyShop(shop.id, {
          ...buildFilters(),
          shopId: shop.id,
        });
        found += result.found;
        imported += result.imported;
        failed += result.failed;
      }

      toast({
        title: t('integrations.etsy.toasts.syncFinishedTitle', 'Etsy sync finished'),
        description: t('integrations.etsy.toasts.syncFinishedDescription', '{{shopName}}: found {{found}}, imported {{imported}}, failed {{failed}}.', {
          shopName: shopId
            ? shops.find((shop) => shop.id === shopId)?.shopName ?? 'Etsy'
            : t('integrations.etsy.import.allShops', 'All shops'),
          found,
          imported,
          failed,
        }),
      });
      await loadOrders();
    } catch (error) {
      logging.error('Failed to sync Etsy import orders', { error, shopId });
      toast({
        title: t('integrations.etsy.toasts.syncFailedTitle', 'Sync failed'),
        description: error instanceof Error ? error.message : undefined,
        variant: 'destructive',
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleMoveSelected = async (options?: { readonly navigateToInbox?: boolean }) => {
    if (selectedOrderIds.length === 0) {
      return false;
    }

    try {
      setMoving(true);
      const result = await updateEtsyImportedOrdersInboxSelection(selectedOrderIds, true);
      toast({
        title: t('integrations.etsy.toasts.ordersMovedToInboxTitle', 'Orders moved to Inbox'),
        description: t('integrations.etsy.toasts.ordersMovedToInboxDescription', '{{count}} Etsy orders are now ready for matching and production setup.', { count: result.count }),
      });
      setSelectedOrderIds([]);
      await loadOrders();
      if (options?.navigateToInbox) {
        navigate('/studio?canvas=etsy-orders');
      }
      return true;
    } catch (error) {
      logging.error('Failed to move Etsy orders to inbox', { error, selectedOrderIds });
      toast({
        title: t('integrations.etsy.toasts.ordersMoveToInboxFailedTitle', 'Could not move orders to Inbox'),
        description: error instanceof Error ? error.message : undefined,
        variant: 'destructive',
      });
      return false;
    } finally {
      setMoving(false);
    }
  };

  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto p-4">
      <div className="space-y-2">
        <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <PackageSearch className="h-3.5 w-3.5" />
          Etsy
        </div>
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">
          {t('integrations.etsy.import.title', 'Etsy Import')}
        </h1>
        <p className="max-w-3xl text-sm text-muted-foreground">
          {t('integrations.etsy.import.description', 'Sync Etsy receipts with filters, review the local import table, and move only selected orders into the Inbox.')}
        </p>
      </div>

      <EtsyWorkspaceNav
        activeMode={WORKSPACE_CANVAS_MODES.etsyImport}
        beforeNavigate={async (target) => {
          if (target.mode !== WORKSPACE_CANVAS_MODES.etsyOrders) {
            return true;
          }

          if (selectedOrderIds.length === 0) {
            return true;
          }

          return handleMoveSelected();
        }}
      />

      <Card>
        <CardHeader>
          <CardTitle>{t('integrations.etsy.import.filtersTitle', 'Import filters')}</CardTitle>
          <CardDescription>
            {t('integrations.etsy.import.filtersDescription', 'The same filters are used for Etsy sync and for the local imported-orders table.')}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 lg:grid-cols-[minmax(180px,1fr)_repeat(2,minmax(160px,0.7fr))]">
          <label className="space-y-2 text-sm font-medium">
            <span>{t('integrations.etsy.import.shop', 'Shop')}</span>
            <select
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={shopId}
              onChange={(event) => setShopId(event.target.value)}
            >
              <option value="">{t('integrations.etsy.import.allShops', 'All shops')}</option>
              {shops.map((shop) => (
                <option key={shop.id} value={shop.id}>{shop.shopName}</option>
              ))}
            </select>
          </label>
          <label className="space-y-2 text-sm font-medium">
            <span>{t('integrations.etsy.import.dateFrom', 'From')}</span>
            <Input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
          </label>
          <label className="space-y-2 text-sm font-medium">
            <span>{t('integrations.etsy.import.dateTo', 'To')}</span>
            <Input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
          </label>
          <label className="flex items-center gap-2 text-sm font-medium">
            <input type="checkbox" checked={onlyPaid} onChange={(event) => setOnlyPaid(event.target.checked)} />
            {t('integrations.etsy.import.onlyPaid', 'Only paid')}
          </label>
          <label className="flex items-center gap-2 text-sm font-medium">
            <input type="checkbox" checked={onlyUnshipped} onChange={(event) => setOnlyUnshipped(event.target.checked)} />
            {t('integrations.etsy.import.onlyUnshipped', 'Only not shipped')}
          </label>
          <label className="flex items-center gap-2 text-sm font-medium">
            <input type="checkbox" checked={onlyUndelivered} onChange={(event) => setOnlyUndelivered(event.target.checked)} />
            {t('integrations.etsy.import.onlyUndelivered', 'Only not delivered')}
          </label>
          <div className="flex flex-wrap gap-2 lg:col-span-3">
            <Button type="button" onClick={() => void handleSync()} disabled={shops.length === 0 || syncing}>
              {syncing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <DownloadCloud className="mr-2 h-4 w-4" />}
              {t('integrations.etsy.import.syncMatching', 'Sync matching Etsy orders')}
            </Button>
            <Button type="button" variant="ghost" onClick={handleResetFilters}>
              <RotateCcw className="mr-2 h-4 w-4" />
              {t('integrations.etsy.import.resetFilters', 'Reset filters')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="min-h-0">
        <CardHeader className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <CardTitle>{t('integrations.etsy.import.tableTitle', 'Imported Etsy orders')}</CardTitle>
            <CardDescription>
              {t('integrations.etsy.import.tableDescription', '{{count}} orders match current filters.', { count: filteredOrders.length })}
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              className="w-72"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('integrations.etsy.import.searchPlaceholder', 'Search receipt, buyer, shop, or title')}
            />
            <Button
              type="button"
              onClick={() => void handleMoveSelected({ navigateToInbox: true })}
              disabled={selectedOrderIds.length === 0 || moving}
            >
              {moving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Inbox className="mr-2 h-4 w-4" />}
              {t('integrations.etsy.import.moveSelected', 'Move selected to Inbox')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading && orders.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('common.loading', 'Loading')}
            </div>
          ) : null}

          {!loading && filteredOrders.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-muted/20 px-6 py-14 text-center">
              <PackageSearch className="mx-auto mb-4 h-10 w-10 text-muted-foreground" />
              <h2 className="text-lg font-semibold text-foreground">
                {t('integrations.etsy.import.emptyTitle', 'No Etsy orders match these filters')}
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                {t('integrations.etsy.import.emptyDescription', 'Sync from Etsy or change filters to see imported receipts here.')}
              </p>
            </div>
          ) : null}

          {filteredOrders.length > 0 ? (
            <ScrollArea className="h-[calc(100vh-28rem)] rounded-lg border border-border/60">
              <Table>
                <TableHeader className="sticky top-0 z-20 bg-background shadow-sm">
                  <TableRow>
                    <TableHead className="w-10">
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        onChange={toggleAllVisible}
                        aria-label={t('integrations.etsy.import.selectAll', 'Select all visible unassigned orders')}
                      />
                    </TableHead>
                    <TableHead>{t('integrations.etsy.import.columns.status', 'Status')}</TableHead>
                    <TableHead>{t('integrations.etsy.import.columns.etsyDate', 'Etsy date')}</TableHead>
                    <TableHead>{t('integrations.etsy.import.columns.buyer', 'Buyer')}</TableHead>
                    <TableHead>{t('integrations.etsy.import.columns.destination', 'Destination')}</TableHead>
                    <TableHead>{t('integrations.etsy.import.columns.orderItems', 'Order items')}</TableHead>
                    <TableHead className="text-right">{t('integrations.etsy.import.columns.total', 'Total')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.map((order) => {
                    const alreadyInInbox = order.selectedForInbox;
                    return (
                      <TableRow key={order.id}>
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={alreadyInInbox || selectedOrderIdSet.has(order.id)}
                            disabled={alreadyInInbox}
                            onChange={() => toggleOrder(order.id)}
                            aria-label={t('integrations.etsy.import.selectOrder', 'Select Etsy receipt #{{receiptId}}', { receiptId: order.etsyReceiptId })}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <span className="font-semibold">#{order.etsyReceiptId}</span>
                            <div className="flex flex-wrap gap-1">
                              <Badge
                                variant="outline"
                                className={getStatusTagClassName(order.etsyData.is_paid ? 'success' : 'neutral')}
                              >
                                {order.etsyData.is_paid ? t('integrations.etsy.orderDetails.paid', 'Paid') : t('integrations.etsy.orderDetails.unpaid', 'Unpaid')}
                              </Badge>
                              <Badge
                                variant="outline"
                                className={getStatusTagClassName(order.etsyData.is_shipped ? 'neutral' : 'success')}
                              >
                                {order.etsyData.is_shipped ? t('integrations.etsy.orderDetails.shipped', 'Shipped') : t('integrations.etsy.orderDetails.notShipped', 'Not shipped')}
                              </Badge>
                              {alreadyInInbox ? (
                                <Badge variant="outline" className={getStatusTagClassName('inbox')}>{t('integrations.etsy.import.inInbox', 'In Inbox')}</Badge>
                              ) : null}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{formatEtsyOrderDateTime(order)}</TableCell>
                        <TableCell>
                          <div className="font-medium">{order.etsyData.name || t('integrations.etsy.common.unknownBuyer', 'Unknown buyer')}</div>
                          <div className="text-xs text-muted-foreground">{order.etsyData.buyer_email || t('integrations.etsy.common.noEmail', 'No email')}</div>
                        </TableCell>
                        <TableCell className="max-w-xs text-sm text-muted-foreground">
                          {[order.etsyData.city, order.etsyData.state, order.etsyData.country_iso].filter(Boolean).join(', ') || t('integrations.etsy.common.notAvailable', 'n/a')}
                        </TableCell>
                        <TableCell className="max-w-sm">
                          <div className="line-clamp-2 text-sm">
                            {order.items.map((item) => item.title).join(', ') || t('integrations.etsy.ordersExpanded.noItemDetails', 'Etsy did not return item details for this receipt.')}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {t('integrations.etsy.matching.itemsCount', '{{count}} items', { count: order.items.length })}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-semibold">{formatMoney(order)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </ScrollArea>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};

export default EtsyImportCanvas;
